import {
  Component,
  forwardRef,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  DoCheck,
  IterableDiffers,
  IterableDiffer,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  AbstractControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploadComponent } from '../../../admin/components/file-upload/file-upload.component';
import { Media } from '../../interfaces/settings.interface';
import { Avatar } from '../../interfaces/avatar.interface';

interface MessageItem {
  name: string;
  avatar_image_id: number;
  message: string;
  is_own_message: boolean;
}

interface ScenarioItemAdditionalFieldsForTeamChatData {
  group_name: {
    en: string;
    fr: string;
    id: string;
  };
  messages: {
    en: MessageItem[];
    fr: MessageItem[];
    id: MessageItem[];
  };
}

@Component({
  selector: 'app-messages-component',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MessagesComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    TranslateModule,
    FileUploadComponent,
  ],
})
export class MessagesComponent
  implements OnInit, ControlValueAccessor, OnChanges, DoCheck, OnDestroy
{
  @Input() placeholder: string = 'Enter data';
  @Input() groupNameControlName: string = 'group_name';
  @Input() messagesControlName: string = 'messages';
  @Input() languages: string[] = ['en'];
  @Input() parentFormGroup: FormGroup | null = null;
  @Input() targetFormGroup: string = '';
  @Input() messages: any;
  @Input() group_name: any;
  @Input() media: Media[] = [];
  @Input() avatars: Avatar[] = [];

  form: FormGroup;

  private onChange = (value: any) => {};
  private onTouched = () => {};
  disabled = false;

  private languagesDiffer?: IterableDiffer<string>;

  constructor(private fb: FormBuilder, private differs: IterableDiffers) {
    this.form = this.fb.group({
      group_name: this.fb.group({
        en: [''],
      }),
      messages: this.fb.group({
        en: this.fb.array([]),
      }),
    });
  }

  ngOnInit() {
    if (this.parentFormGroup) {
      let targetGroup: FormGroup;

      if (this.targetFormGroup) {
        targetGroup = this.parentFormGroup.get(
          this.targetFormGroup
        ) as FormGroup;
        if (!targetGroup) {
          this.parentFormGroup.addControl(
            this.targetFormGroup,
            this.fb.group({})
          );
          targetGroup = this.parentFormGroup.get(
            this.targetFormGroup
          ) as FormGroup;
        }
      } else {
        targetGroup = this.parentFormGroup;
      }

      targetGroup.addControl(
        this.groupNameControlName,
        this.form.get('group_name')
      );
      targetGroup.addControl(
        this.messagesControlName,
        this.form.get('messages')
      );
    }

    this.form.valueChanges.subscribe((value) => {
      this.onChange({
        group_name: value.group_name,
        messages: value.messages,
      });
      this.onTouched();
    });

    this.languagesDiffer = this.differs.find(this.languages).create<string>();
    this.updateLanguages();

    if (this.group_name) {
      this.form.patchValue(
        { group_name: this.group_name },
        { emitEvent: false }
      );
    }

    if (this.messages) {
      this.patchMessages(this.messages);
    }

    this.languages.forEach((lang) => {
      const messagesArray = this.getMessagesArray(lang);
      if (messagesArray.length === 0) {
        this.addMessage(lang);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['languages'] && !changes['languages'].firstChange) {
      setTimeout(() => {
        this.updateLanguages();
        this.languages.forEach((lang) => {
          const messagesArray = this.getMessagesArray(lang);
          if (messagesArray.length === 0) {
            this.addMessage(lang);
          }
        });
      }, 0);
    }

    if (changes['group_name'] && !changes['group_name'].firstChange) {
      this.form.patchValue(
        { group_name: this.group_name },
        { emitEvent: false }
      );
    }

    if (changes['messages'] && !changes['messages'].firstChange) {
      this.patchMessages(this.messages);
    }
  }

  removeAvatar(avatarControl: AbstractControl | null) {
    if (avatarControl) {
      avatarControl.setValue('');
    }
  }

  ngDoCheck(): void {
    if (this.languagesDiffer?.diff(this.languages)) {
      this.updateLanguages();
    }
  }

  ngOnDestroy() {
    if (this.parentFormGroup) {
      let targetGroup: FormGroup;

      if (this.targetFormGroup) {
        targetGroup = this.parentFormGroup.get(
          this.targetFormGroup
        ) as FormGroup;
        if (targetGroup) {
          targetGroup.removeControl(this.groupNameControlName);
          targetGroup.removeControl(this.messagesControlName);
        }
      } else {
        this.parentFormGroup.removeControl(this.groupNameControlName);
        this.parentFormGroup.removeControl(this.messagesControlName);
      }
    }
  }

  private updateLanguages() {
    const currentLanguages = Object.keys(
      this.form.get('group_name')?.value ?? {}
    );

    this.languages.forEach((lang) => {
      if (!currentLanguages.includes(lang)) {
        this.addLanguage(lang);
      }
    });

    currentLanguages.forEach((lang) => {
      if (!this.languages.includes(lang)) {
        this.removeLanguage(lang);
      }
    });

    this.form.updateValueAndValidity();
  }

  private addLanguage(lang: string) {
    try {
      const groupNameControl = this.form.get('group_name') as FormGroup;
      if (!groupNameControl.contains(lang)) {
        groupNameControl.addControl(lang, new FormControl(''));
      }

      const messagesControl = this.form.get('messages') as FormGroup;
      if (!messagesControl.contains(lang)) {
        messagesControl.addControl(lang, this.fb.array([]));
      }

      const messagesArray = this.getMessagesArray(lang);
      if (messagesArray.length === 0) {
        this.addMessage(lang);
      }

      this.messagesArrayCache.delete(lang);
    } catch (error) {
      console.warn(`Error adding language ${lang}:`, error);
    }
  }

  private removeLanguage(lang: string) {
    try {
      const groupNameControl = this.form.get('group_name') as FormGroup;
      const messagesControl = this.form.get('messages') as FormGroup;

      if (groupNameControl.contains(lang)) {
        groupNameControl.removeControl(lang);
      }
      if (messagesControl.contains(lang)) {
        messagesControl.removeControl(lang);
      }

      this.messagesArrayCache.delete(lang);
    } catch (error) {
      console.warn(`Error removing language ${lang}:`, error);
    }
  }

  writeValue(value: any): void {
    if (value) {
      if (value.group_name || value.messages) {
        if (value.group_name) {
          this.form.patchValue(
            {
              group_name: value.group_name,
            },
            { emitEvent: false }
          );
        }

        if (value.messages) {
          this.patchMessages(value.messages);
        }
      } else {
        this.patchFormValue(value);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  addMessage(lang: string): void {
    try {
      const messageGroup = this.fb.group({
        name: [''],
        avatar_image_id: [''],
        message: [''],
        is_own_message: [false],
      });

      const messagesArray = this.getMessagesArray(lang);

      if (messagesArray && messagesArray instanceof FormArray) {
        messagesArray.push(messageGroup);

        this.messagesArrayCache.delete(lang);
      } else {
        console.warn(
          `Messages array for ${lang} is not a FormArray:`,
          messagesArray
        );
      }
    } catch (error) {
      console.warn(`Error adding message for language ${lang}:`, error);
    }
  }

  removeMessage(lang: string, index: number): void {
    try {
      const messagesArray = this.getMessagesArray(lang);
      if (
        messagesArray &&
        messagesArray instanceof FormArray &&
        messagesArray.length > 1
      ) {
        messagesArray.removeAt(index);

        this.messagesArrayCache.delete(lang);
      }
    } catch (error) {
      console.warn(`Error removing message for language ${lang}:`, error);
    }
  }

  private messagesArrayCache = new Map<string, FormArray>();

  private currentAvatarControl: FormControl | null = null;
  private currentAvatarLang: string | null = null;
  private currentAvatarIndex: number | null = null;

  getMessagesArray(lang: string): FormArray {
    if (this.messagesArrayCache.has(lang)) {
      return this.messagesArrayCache.get(lang)!;
    }

    const messagesControl = this.form.get('messages') as FormGroup;
    if (messagesControl?.contains(lang)) {
      const array = messagesControl.get(lang) as FormArray;

      this.messagesArrayCache.set(lang, array);
      return array;
    }

    return this.fb.array([]);
  }

  getGroupNameControl(lang: string): FormControl {
    const groupNameControl = this.form.get('group_name') as FormGroup;
    if (groupNameControl?.contains(lang)) {
      return groupNameControl.get(lang) as FormControl;
    }

    return new FormControl('');
  }

  isLanguageAvailable(lang: string): boolean {
    const groupNameControl = this.form.get('group_name') as FormGroup;
    const messagesControl = this.form.get('messages') as FormGroup;

    const hasGroupName = groupNameControl?.contains(lang);
    const hasMessages = messagesControl?.contains(lang);

    return hasGroupName && hasMessages;
  }

  private patchFormValue(
    value: ScenarioItemAdditionalFieldsForTeamChatData
  ): void {
    this.languages.forEach((lang) => {
      const messagesArray = this.getMessagesArray(lang);
      while (messagesArray.length !== 0) {
        messagesArray.removeAt(0);
      }
    });

    if (value.group_name) {
      this.form.patchValue(
        {
          group_name: value.group_name,
        },
        { emitEvent: false }
      );
    }

    if (value.messages) {
      this.patchMessages(value.messages);
    }

    this.languages.forEach((lang) => {
      const messagesArray = this.getMessagesArray(lang);
      if (messagesArray.length === 0) {
        this.addMessage(lang);
      }
    });
  }

  private patchMessages(messages: any): void {
    this.languages.forEach((lang) => {
      const messagesArray = this.getMessagesArray(lang);
      while (messagesArray.length !== 0) {
        messagesArray.removeAt(0);
      }
    });

    this.languages.forEach((lang) => {
      if (messages[lang]) {
        messages[lang].forEach((message: any) => {
          const messageGroup = this.fb.group({
            name: [message.name ?? ''],
            avatar_image_id: [message.avatar_image_id ?? ''],
            message: [message.message ?? ''],
            is_own_message: [message.is_own_message ?? false],
          });
          const messagesArray = this.getMessagesArray(lang);
          messagesArray.push(messageGroup);
        });
      }
    });

    this.languages.forEach((lang) => {
      const messagesArray = this.getMessagesArray(lang);
      if (messagesArray.length === 0) {
        this.addMessage(lang);
      }
    });
  }

  onAvatarFile(event: Media) {
    if (this.currentAvatarControl && event?.id) {
      this.currentAvatarControl.setValue(event.id);
      if (!this.media.some((m) => m.id === event.id)) {
        this.media.push(event);
      }
      const lang = this.currentAvatarLang;
      this.clearCurrentAvatarContext();
      if (lang) {
        this.messagesArrayCache.delete(lang);
      }
    } else {
      console.warn('No current avatar control or invalid event:', event);
    }
  }

  selectAvatarFromList(avatar: Avatar): void {
    if (!this.currentAvatarControl || !avatar?.id) {
      return;
    }
    const avatarId = Number(avatar.id);
    this.currentAvatarControl.setValue(avatarId);
    if (!this.media.some((m) => m.id === avatarId)) {
      this.media.push({
        id: avatarId,
        name: avatar.name,
        original_url: avatar.original_url,
        link: avatar.link,
        mime_type: '',
      });
    }
    const lang = this.currentAvatarLang;
    this.clearCurrentAvatarContext();
    if (lang) {
      this.messagesArrayCache.delete(lang);
    }
  }

  openAvatarUpload(lang: string, index: number): void {
    this.setCurrentControl(lang, index);
    this.uploadFile?.clickInput();
  }

  @ViewChild('uploadFile') uploadFile: { clickInput: () => void };

  private clearCurrentAvatarContext(): void {
    this.currentAvatarControl = null;
    this.currentAvatarLang = null;
    this.currentAvatarIndex = null;
  }

  setCurrentControl(lang: string, index: number) {
    const messagesArray = this.getMessagesArray(lang);
    if (messagesArray?.controls[index]) {
      const messageControl = messagesArray.controls[index] as FormGroup;
      this.currentAvatarControl = messageControl.get(
        'avatar_image_id'
      ) as FormControl;
      this.currentAvatarLang = lang;
      this.currentAvatarIndex = index;
    }
  }

  getMediaUrl(id: number | string | null): string {
    if (!id) return '';
    const numId = Number(id);
    const media = this.media.find((m) => m.id === numId);
    if (media?.link) {
      return media.link;
    }
    const avatar = this.avatars.find((a) => Number(a.id) === numId);
    return avatar?.link || '';
  }
}
